import { type Expiration } from '../../../types/Expiration';
import {
  type CardNetworkMeta,
  defaultNumberFormat,
  getCardNetworkMetaByNumber,
  getMaxCardLengthByNumber,
} from './cardNetworksMeta';
import { sanitizeCardExpiry, sanitizeCardNumber } from './sanitizeUtils';

const undefinedExpiration: Expiration = {
  month: undefined,
  year: undefined,
};

export const transformExpireStrToObj = (expiry: string): Expiration => {
  if (typeof expiry !== 'string') {
    return undefinedExpiration;
  }

  const date: number[] = expiry
    .split('/', 2)
    .map((part) => part.replace(/\D/g, ''))
    .map((part) => parseInt(part, 10));
  const month: number = date[0];
  let year: number = date[1];

  if (!Number.isInteger(month) || !Number.isInteger(year)) {
    return undefinedExpiration;
  }

  if (year < 100) {
    const fullYear = new Date().getFullYear();
    const prefix = String(fullYear).slice(0, 2);

    year = parseInt(`${prefix}${year < 10 ? '0' : ''}${year}`, 10);
  }

  return {
    month,
    year,
  };
};

export const transformExpireObjToStr = (expiration: Expiration): string => {
  const monthStr: string = expiration.month >= 10 ? String(expiration.month) : `0${expiration.month}`;
  const yearStr = String(expiration.year % 100);

  return formatCardExpiry(`${monthStr} / ${yearStr}`);
};

export const formatCardExpiry = (expiry: string): string => {
  const sanitizedExpiry: string = sanitizeCardExpiry(expiry);
  const matchResult: string[] = sanitizedExpiry.match(/^(\d{1,2}\d?)?(\/)?(\d{1,4})?$/);

  if (matchResult === null) {
    return '';
  }

  let month = matchResult[1] || '';
  let separation = matchResult[2] || '';
  let year = matchResult[3] || '';

  if (!month && !year) {
    return '';
  }

  if (month.length === 3) {
    year = `${month[2]}${year}`;
    month = month.slice(0, 2);
  }

  if (month.length === 1 && parseInt(month, 10) > 1) {
    month = `0${month}`;
  }

  if (month.length === 2 && parseInt(month, 10) > 12) {
    // e.g. when there is expiry date with month and year (can be empty or not full),
    // and user add more digits to month
    // e.g when enter '2' into '12 ^/ ', should be '12 / 2^'
    const tooBigMonthNumber = month;
    month = `0` + tooBigMonthNumber[0];

    if (year.length < 2) {
      year = `${tooBigMonthNumber[1]}${year}`;
    }
  }

  if (year.length > 0) {
    separation = ' / ';
  } else if (separation === ' / ') {
    month = month.substring(0, 1);
    separation = '';
  } else if (month.length === 2 || separation.length > 0) {
    separation = ' / ';
  } else if (month.length === 1 && month !== '0' && month !== '1') {
    separation = ' / ';
  }

  if (year.length === 4) {
    year = year.slice(2, 4);
  }

  return `${month}${separation}${year}`;
};

const getGroupsFromAMatch = (match: any[], format: RegExp): string[] =>
  format.global ? match : match.slice(1).filter((group) => typeof group === 'string');

export const formatCardNumber = (cardNumber: string): string => {
  const sanitizedCardNumber: string = sanitizeCardNumber(cardNumber);
  const cardNetworkMeta: CardNetworkMeta = getCardNetworkMetaByNumber(sanitizedCardNumber);

  const maxLength: number = getMaxCardLengthByNumber(sanitizedCardNumber);
  const format: RegExp = cardNetworkMeta ? cardNetworkMeta.format : defaultNumberFormat;
  const slicedNumber: string = sanitizedCardNumber.slice(0, maxLength);
  const matchResult: string[] | null = slicedNumber.match(format);

  if (matchResult === null) {
    return '';
  }

  const numberGroups: string[] = getGroupsFromAMatch(matchResult, format);
  let formattedCardNumber: string = numberGroups.join(' ');

  if (
    slicedNumber.length < maxLength &&
    // new group appear if next digit will be typed
    getGroupsFromAMatch((slicedNumber + '0').match(format), format).length > numberGroups.length
  ) {
    formattedCardNumber += ' ';
  }

  return formattedCardNumber;
};
